import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import AdminButton from '../../../components/admin/AdminButton';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import 'react-tabs/style/react-tabs.css';
import { createFedexShipment, getFedexShipmentByTrackingNumber } from '../../../services/fedexApi';
import { supabase } from '../../../supabaseClient';

const FedexShipmentForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!id;
  
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Données fixes de l&apos;expéditeur (Consulat)
  // Ces informations seront utilisées lors de l&apos;implémentation réelle de l&apos;API FedEx
  /* 
  const shipperInfo = {
    name: "Consulat G Cote d'Ivoire Paris",
    company: "Consulat G Cote d'Ivoire Paris",
    address1: "19 rue de la pepiniere",
    address2: "",
    address3: "",
    city: "PARIS",
    postalCode: "75008",
    country: "France",
    phone: "0181726604",
    email: "consulatcistandard@outlook.fr"
  };
  */
  
  // Services FedEx disponibles
  const fedexServices = [
    { id: "STANDARD_OVERNIGHT", name: "FedEx Standard Overnight" },
    { id: "FEDEX_PRIORITY", name: "FedEx Priority" },
    { id: "FEDEX_EXPRESS_SAVER", name: "FedEx Express Saver" },
    { id: "FEDEX_GROUND", name: "FedEx Ground" },
    { id: "INTERNATIONAL_PRIORITY", name: "FedEx International Priority" },
    { id: "INTERNATIONAL_ECONOMY", name: "FedEx International Economy" }
  ];
  
  // Options de signature
  const signatureOptions = [
    { id: "DIRECT", name: "Signature directe requise" },
    { id: "INDIRECT", name: "Signature indirecte requise" },
    { id: "ADULT", name: "Signature d&apos;un adulte requise" },
    { id: "NO_SIGNATURE_REQUIRED", name: "Aucune signature requise" }
  ];
  
  // Options d&apos;enlèvement/dépôt
  const pickupOptions = [
    { id: "USE_SCHEDULED_PICKUP", name: "J&apos;ai déjà programmé un enlèvement en ma localité" },
    { id: "DROPOFF_AT_FEDEX_LOCATION", name: "Je déposerai le colis dans une agence FedEx" }
  ];
  
  // État initial du formulaire d&apos;expédition FedEx
  const [formData, setFormData] = useState({
    // Informations du destinataire
    recipient_firstname: '',
    recipient_lastname: '',
    recipient_company: '',
    recipient_address: '',
    recipient_address2: '',
    recipient_city: '',
    recipient_zipcode: '',
    recipient_country: 'France',
    recipient_phone: '',
    recipient_email: '',
    
    // Informations du colis
    package_type: 'FEDEX_ENVELOPE',
    package_weight: 0.5,
    package_count: 1,
    
    // Options FedEx
    fedex_service_type: 'FEDEX_PRIORITY',
    fedex_signature_option: 'INDIRECT',
    pickup_type: 'USE_SCHEDULED_PICKUP',
    
    // Dates
    fedex_ship_date: format(new Date(), 'yyyy-MM-dd'),
    
    // Options supplémentaires
    send_notification: true,
    notification_email: '',
    reference_number: '',
    
    // Statut
    status: 'created',
  });
  
  // Fonction pour calculer la date de livraison estimée
  const getEstimatedDeliveryDate = () => {
    // Logique simplifiée pour estimer la date de livraison
    const shipDate = new Date(formData.fedex_ship_date);
    let deliveryDays = 1; // Par défaut, 1 jour
    
    switch (formData.fedex_service_type) {
      case 'FEDEX_PRIORITY':
        deliveryDays = 1;
        break;
      case 'STANDARD_OVERNIGHT':
        deliveryDays = 1;
        break;
      case 'FEDEX_EXPRESS_SAVER':
        deliveryDays = 3;
        break;
      case 'FEDEX_GROUND':
        deliveryDays = 5;
        break;
      case 'INTERNATIONAL_PRIORITY':
        deliveryDays = 2;
        break;
      case 'INTERNATIONAL_ECONOMY':
        deliveryDays = 4;
        break;
      default:
        deliveryDays = 1;
    }
    
    const deliveryDate = new Date(shipDate);
    deliveryDate.setDate(shipDate.getDate() + deliveryDays);
    
    return format(deliveryDate, 'dd MMMM yyyy', { locale: fr });
  };
  
  // Fonction pour rechercher des utilisateurs
  const searchUsers = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }
    
    try {
      // Utiliser la fonction de recherche Supabase
      const results = await searchUsersFromSupabase(query);
      
      if (results && results.length > 0) {
        setSearchResults(results);
        setShowSearchResults(true);
      } else {
        // Si aucun résultat n'est trouvé, utiliser des données de démonstration
        const mockResults = [
          {
            id: '1',
            firstname: 'Jean',
            lastname: 'Dupont',
            email: 'jean.dupont@example.com',
            phone: '0123456789',
            address: '123 Rue de Paris',
            city: 'Paris',
            zipcode: '75001',
            country: 'France',
            company: 'Entreprise A'
          },
          {
            id: '2',
            firstname: 'Marie',
            lastname: 'Martin',
            email: 'marie.martin@example.com',
            phone: '0987654321',
            address: '456 Avenue des Champs-Élysées',
            city: 'Paris',
            zipcode: '75008',
            country: 'France',
            company: 'Entreprise B'
          }
        ];
        
        const filteredResults = mockResults.filter(user => 
          user.firstname.toLowerCase().includes(query.toLowerCase()) || 
          user.lastname.toLowerCase().includes(query.toLowerCase()) ||
          user.email.toLowerCase().includes(query.toLowerCase())
        );
        
        setSearchResults(filteredResults);
        setShowSearchResults(true);
      }
    } catch (error) {
      console.error('Erreur lors de la recherche d\'utilisateurs:', error);
      toast.error('Erreur lors de la recherche d&apos;utilisateurs');
    }
  };
  
  // Gestion du changement dans les champs de formulaire
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  // Gestion de la recherche d'utilisateurs
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    searchUsers(query);
  };
  
  // Sélection d'un utilisateur dans les résultats de recherche
  const handleSelectUser = (user) => {
    setFormData(prev => ({
      ...prev,
      recipient_firstname: user.firstname || '',
      recipient_lastname: user.lastname || '',
      recipient_company: user.company || '',
      recipient_address: user.address || '',
      recipient_city: user.city || '',
      recipient_zipcode: user.zipcode || '',
      recipient_country: user.country || 'France',
      recipient_phone: user.phone || '',
      recipient_email: user.email || '',
      notification_email: user.email || ''
    }));
    
    setSearchQuery('');
    setShowSearchResults(false);
  };
  
  // Données fixes de l'expéditeur (Consulat)
  const shipperInfo = {
    name: "Consulat G Cote d'Ivoire Paris",
    company: "Consulat G Cote d'Ivoire Paris",
    address1: "19 rue de la pepiniere",
    address2: "",
    address3: "",
    city: "PARIS",
    postalCode: "75008",
    country: "France",
    phone: "0181726604",
    email: "consulatcistandard@outlook.fr"
  };
  
  // Charger les détails d'une expédition existante si en mode édition
  useEffect(() => {
    if (isEditMode && id) {
      const loadShipmentDetails = async () => {
        try {
          setLoading(true);
          // Appel au service pour récupérer les détails de l'expédition
          const shipmentDetails = await getFedexShipmentByTrackingNumber(id);
          
          if (shipmentDetails) {
            // Mise à jour du formulaire avec les détails récupérés
            setFormData(prevFormData => ({
              ...prevFormData,
              recipient_firstname: shipmentDetails.recipient_firstname || '',
              recipient_lastname: shipmentDetails.recipient_lastname || '',
              recipient_company: shipmentDetails.recipient_company || '',
              recipient_address: shipmentDetails.recipient_address || '',
              recipient_address2: shipmentDetails.recipient_address2 || '',
              recipient_city: shipmentDetails.recipient_city || '',
              recipient_zipcode: shipmentDetails.recipient_zipcode || '',
              recipient_country: shipmentDetails.recipient_country || 'France',
              recipient_phone: shipmentDetails.recipient_phone || '',
              recipient_email: shipmentDetails.recipient_email || '',
              
              package_type: shipmentDetails.package_type || 'FEDEX_ENVELOPE',
              package_weight: shipmentDetails.package_weight || 0.5,
              package_count: shipmentDetails.package_count || 1,
              
              fedex_service_type: shipmentDetails.fedex_service_type || 'PRIORITY_OVERNIGHT',
              fedex_signature_option: shipmentDetails.fedex_signature_option || 'INDIRECT',
              pickup_type: shipmentDetails.pickup_type || 'USE_SCHEDULED_PICKUP',
              
              fedex_ship_date: shipmentDetails.fedex_ship_date || format(new Date(), 'yyyy-MM-dd'),
              
              send_notification: shipmentDetails.send_notification !== undefined ? shipmentDetails.send_notification : true,
              notification_email: shipmentDetails.notification_email || '',
              reference_number: shipmentDetails.reference_number || '',
              
              status: shipmentDetails.status || 'pending',
              tracking_number: shipmentDetails.tracking_number || ''
            }));
          } else {
            toast.error('Impossible de trouver les détails de cette expédition');
            navigate('/admin/fedex-shipments');
          }
        } catch (error) {
          console.error('Erreur lors du chargement des détails de l&apos;expédition:', error);
          toast.error('Erreur lors du chargement des détails de l&apos;expédition');
          navigate('/admin/fedex-shipments');
        } finally {
          setLoading(false);
        }
      };
      
      loadShipmentDetails();
    }
  }, [id, isEditMode, navigate]);
  
  // Fonction pour récupérer les utilisateurs depuis Supabase
  const searchUsersFromSupabase = async (query) => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('id, firstname, lastname, email, phone, address, city, zipcode, country, company')
        .or(`firstname.ilike.%${query}%,lastname.ilike.%${query}%,email.ilike.%${query}%`)
        .limit(5);
      
      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Erreur lors de la recherche d\'utilisateurs:', error);
      return [];
    }
  };
  
  // Fonction pour créer une expédition FedEx via l'API
  const createFedExShipment = async () => {
    try {
      setLoading(true);
      
      // Construction de la requête pour l'API FedEx
      const shipmentRequest = {
        shipper: {
          contact: {
            personName: shipperInfo.name,
            phoneNumber: shipperInfo.phone,
            emailAddress: shipperInfo.email
          },
          address: {
            streetLines: [
              shipperInfo.address1,
              shipperInfo.address2
            ].filter(Boolean),
            city: shipperInfo.city,
            stateOrProvinceCode: '',
            postalCode: shipperInfo.postalCode,
            countryCode: 'FR'
          }
        },
        recipients: [
          {
            contact: {
              personName: `${formData.recipient_firstname} ${formData.recipient_lastname}`,
              phoneNumber: formData.recipient_phone,
              emailAddress: formData.recipient_email
            },
            address: {
              streetLines: [
                formData.recipient_address,
                formData.recipient_address2
              ].filter(Boolean),
              city: formData.recipient_city,
              stateOrProvinceCode: '',
              postalCode: formData.recipient_zipcode,
              countryCode: 'FR'
            }
          }
        ],
        shipDatestamp: formData.fedex_ship_date,
        serviceType: formData.fedex_service_type,
        packagingType: formData.package_type,
        pickupType: formData.pickup_type,
        blockInsightVisibility: false,
        shippingChargesPayment: {
          paymentType: 'SENDER'
        },
        labelSpecification: {
          imageType: 'PDF',
          labelStockType: 'PAPER_85X11_TOP_HALF_LABEL'
        },
        requestedPackageLineItems: [
          {
            weight: {
              units: 'KG',
              value: formData.package_weight
            }
          }
        ],
        deliveryInstructions: 'Livraison standard'
      };
      
      try {
        // Appel au service pour créer l'expédition
        const response = await createFedexShipment(shipmentRequest);
        
        // Mise à jour du formulaire avec les informations de l'expédition
        setFormData(prev => ({
          ...prev,
          status: 'created',
          fedex_response: response,
          tracking_number: response.output?.shipmentResults?.completedShipmentDetail?.masterTrackingNumber || ''
        }));
        
        toast.success('Expédition FedEx créée avec succès');
        navigate('/admin/fedex-shipments');
      } catch (apiError) {
        console.error('Erreur API FedEx:', apiError);
        toast.error(`Erreur lors de la création de l'expédition: ${apiError.message || 'Erreur inconnue'}`);
        
        // En mode développement, simuler une réponse réussie pour continuer les tests
        if (process.env.NODE_ENV === 'development') {
          console.warn('Mode développement: simulation d\'une réponse réussie');
          const mockResponse = {
            transactionId: 'mock-transaction-id',
            output: {
              shipmentResults: {
                shipmentDocuments: [
                  {
                    contentType: 'application/pdf',
                    docType: 'SHIPPING_LABEL',
                    encodedLabel: 'base64-encoded-label-would-be-here'
                  }
                ],
                completedShipmentDetail: {
                  masterTrackingNumber: '794644915445',
                  serviceDescription: {
                    serviceType: formData.fedex_service_type,
                    description: 'FedEx Priority Overnight'
                  },
                  shipmentRating: {
                    shipmentRateDetails: [
                      {
                        totalNetCharge: 25.50
                      }
                    ]
                  }
                }
              }
            }
          };
          
          setFormData(prev => ({
            ...prev,
            status: 'created',
            fedex_response: mockResponse,
            tracking_number: mockResponse.output.shipmentResults.completedShipmentDetail.masterTrackingNumber
          }));
          
          toast.success('(DEV) Expédition FedEx simulée avec succès');
          navigate('/admin/fedex-shipments');
        }
      }
    } catch (error) {
      console.error('Erreur lors de la création de l&apos;expédition FedEx:', error);
      toast.error('Erreur lors de la création de l&apos;expédition FedEx');
    } finally {
      setLoading(false);
    }
  };
  
  // Soumission du formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation basique du formulaire
    if (!formData.recipient_firstname || !formData.recipient_lastname || !formData.recipient_address || 
        !formData.recipient_city || !formData.recipient_zipcode || !formData.recipient_country) {
      toast.error('Veuillez remplir tous les champs obligatoires du destinataire');
      return;
    }
    
    // Création de l'expédition FedEx
    await createFedExShipment();
  };
  
  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-2xl font-bold mb-6">
        {isEditMode ? 'Modifier l&apos;expédition FedEx' : 'Nouvelle expédition FedEx'}
      </h2>
      
      <form onSubmit={handleSubmit}>
        <Tabs selectedIndex={activeTab} onSelect={index => setActiveTab(index)}>
          <TabList>
            <Tab>Destinataire</Tab>
            <Tab>Options d&apos;expédition</Tab>
            <Tab>Détails du colis</Tab>
          </TabList>
          
          {/* Onglet 1: Destinataire */}
          <TabPanel>
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Rechercher un contact
              </label>
              <div className="relative">
                <input
                  type="text"
                  className="tracking-input w-full"
                  placeholder="Rechercher par nom, prénom ou email..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                />
                
                {showSearchResults && searchResults.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
                    {searchResults.map(user => (
                      <div
                        key={user.id}
                        className="p-2 hover:bg-gray-100 cursor-pointer"
                        onClick={() => handleSelectUser(user)}
                      >
                        <div className="font-medium">{user.firstname} {user.lastname}</div>
                        <div className="text-sm text-gray-600">{user.email}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="recipient_firstname">
                  Prénom*
                </label>
                <input
                  id="recipient_firstname"
                  name="recipient_firstname"
                  type="text"
                  className="tracking-input w-full"
                  value={formData.recipient_firstname}
                  onChange={handleChange}
                  required
                />
              </div>
              
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="recipient_lastname">
                  Nom*
                </label>
                <input
                  id="recipient_lastname"
                  name="recipient_lastname"
                  type="text"
                  className="tracking-input w-full"
                  value={formData.recipient_lastname}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
            
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="recipient_company">
                Entreprise
              </label>
              <input
                id="recipient_company"
                name="recipient_company"
                type="text"
                className="tracking-input w-full"
                value={formData.recipient_company}
                onChange={handleChange}
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="recipient_address">
                Adresse*
              </label>
              <input
                id="recipient_address"
                name="recipient_address"
                type="text"
                className="tracking-input w-full"
                value={formData.recipient_address}
                onChange={handleChange}
                required
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="recipient_address2">
                Complément d&apos;adresse
              </label>
              <input
                id="recipient_address2"
                name="recipient_address2"
                type="text"
                className="tracking-input w-full"
                value={formData.recipient_address2}
                onChange={handleChange}
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="recipient_city">
                  Ville*
                </label>
                <input
                  id="recipient_city"
                  name="recipient_city"
                  type="text"
                  className="tracking-input w-full"
                  value={formData.recipient_city}
                  onChange={handleChange}
                  required
                />
              </div>
              
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="recipient_zipcode">
                  Code postal*
                </label>
                <input
                  id="recipient_zipcode"
                  name="recipient_zipcode"
                  type="text"
                  className="tracking-input w-full"
                  value={formData.recipient_zipcode}
                  onChange={handleChange}
                  required
                />
              </div>
              
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="recipient_country">
                  Pays*
                </label>
                <input
                  id="recipient_country"
                  name="recipient_country"
                  type="text"
                  className="tracking-input w-full"
                  value={formData.recipient_country}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="recipient_phone">
                  Téléphone*
                </label>
                <input
                  id="recipient_phone"
                  name="recipient_phone"
                  type="tel"
                  className="tracking-input w-full"
                  value={formData.recipient_phone}
                  onChange={handleChange}
                  required
                />
              </div>
              
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="recipient_email">
                  Email*
                </label>
                <input
                  id="recipient_email"
                  name="recipient_email"
                  type="email"
                  className="tracking-input w-full"
                  value={formData.recipient_email}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
          </TabPanel>
          
          {/* Onglet 2: Options d'expédition */}
          <TabPanel>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="fedex_service_type">
                  Service FedEx*
                </label>
                <select
                  id="fedex_service_type"
                  name="fedex_service_type"
                  className="tracking-input w-full"
                  value={formData.fedex_service_type}
                  onChange={handleChange}
                  required
                >
                  {fedexServices.map(service => (
                    <option key={service.id} value={service.id}>
                      {service.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="fedex_signature_option">
                  Option de signature
                </label>
                <select
                  id="fedex_signature_option"
                  name="fedex_signature_option"
                  className="tracking-input w-full"
                  value={formData.fedex_signature_option}
                  onChange={handleChange}
                >
                  {signatureOptions.map(option => (
                    <option key={option.id} value={option.id}>
                      {option.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="pickup_type">
                  Type d&apos;enlèvement
                </label>
                <select
                  id="pickup_type"
                  name="pickup_type"
                  className="tracking-input w-full"
                  value={formData.pickup_type}
                  onChange={handleChange}
                >
                  {pickupOptions.map(option => (
                    <option key={option.id} value={option.id}>
                      {option.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="fedex_ship_date">
                  Date d&apos;expédition
                </label>
                <input
                  id="fedex_ship_date"
                  name="fedex_ship_date"
                  type="date"
                  className="tracking-input w-full"
                  value={formData.fedex_ship_date}
                  onChange={handleChange}
                />
              </div>
            </div>
            
            <div className="mt-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="reference_number">
                Numéro de référence
              </label>
              <input
                id="reference_number"
                name="reference_number"
                type="text"
                className="tracking-input w-full"
                value={formData.reference_number}
                onChange={handleChange}
              />
            </div>
            
            <div className="mt-4">
              <p className="text-sm text-gray-700 mb-2">
                Date de livraison estimée: <span className="font-semibold">{getEstimatedDeliveryDate()}</span>
              </p>
            </div>
            
            <div className="mt-4">
              <div className="flex items-center">
                <input
                  id="send_notification"
                  name="send_notification"
                  type="checkbox"
                  className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                  checked={formData.send_notification}
                  onChange={(e) => setFormData(prev => ({ ...prev, send_notification: e.target.checked }))}
                />
                <label htmlFor="send_notification" className="ml-2 block text-sm text-gray-900">
                  Envoyer une notification au destinataire
                </label>
              </div>
              
              {formData.send_notification && (
                <div className="mt-2">
                  <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="notification_email">
                    Email de notification
                  </label>
                  <input
                    id="notification_email"
                    name="notification_email"
                    type="email"
                    className="tracking-input w-full"
                    value={formData.notification_email}
                    onChange={handleChange}
                  />
                </div>
              )}
            </div>
          </TabPanel>
          
          {/* Onglet 3: Détails du colis */}
          <TabPanel>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="package_type">
                  Conditionnement
                </label>
                <select
                  id="package_type"
                  name="package_type"
                  className="tracking-input w-full"
                  value={formData.package_type}
                  onChange={handleChange}
                >
                  <option value="FEDEX_ENVELOPE">FedEx Envelope</option>
                  <option value="FEDEX_PAK">FedEx Pak</option>
                  <option value="FEDEX_BOX">FedEx Box</option>
                  <option value="FEDEX_TUBE">FedEx Tube</option>
                  <option value="YOUR_PACKAGING">Votre emballage</option>
                </select>
              </div>
              
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="package_count">
                  Nombre de colis
                </label>
                <input
                  id="package_count"
                  name="package_count"
                  type="number"
                  min="1"
                  className="tracking-input w-full"
                  value={formData.package_count}
                  onChange={handleChange}
                />
              </div>
              
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="package_weight">
                  Poids (kg)
                </label>
                <input
                  id="package_weight"
                  name="package_weight"
                  type="number"
                  step="0.1"
                  min="0.1"
                  className="tracking-input w-full"
                  value={formData.package_weight}
                  onChange={handleChange}
                />
                <p className="text-sm text-gray-500 mt-1">
                  Limite: 0,5 kg par enveloppe FedEx
                </p>
              </div>
            </div>
          </TabPanel>
        </Tabs>
        
        <div className="flex justify-between mt-8">
          <AdminButton
            type="outline"
            onClick={() => {
              toast.success('Opération annulée');
              navigate('/admin/fedex-shipments');
            }}
            disabled={loading}
          >
            Annuler
          </AdminButton>
          
          <button 
            type="submit"
            className="btn-modern btn-primary-modern px-8"
            disabled={loading}
          >
            {loading ? (
              <span className="flex items-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Traitement en cours...
              </span>
            ) : (
              isEditMode ? 'Mettre à jour' : "Créer l'expédition"
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default FedexShipmentForm;
